/**
 * Authenticated API Client for Briefcase AI Telemetry Dashboard
 *
 * Provides centralized API communication with automatic JWT token management,
 * request/response interceptors, error handling, and security features.
 *
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401 responses
 * - Request/response logging and monitoring
 * - Error handling with retry logic
 * - Request deduplication and caching
 * - Security headers and validation
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiResponse, ApiError } from '../../../contracts/types';
import { getApiConfig } from '../../config/api-config';

interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  retryCount?: number;
  dedupeKey?: string;
  cacheKey?: string;
  cacheTTL?: number;
}

interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  maxRetries: number;
  retryDelay: number;
  enableDeduplication: boolean;
  enableCaching: boolean;
  enableLogging: boolean;
}

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

export class ApiClient {
  private client: AxiosInstance;
  private config: ApiClientConfig;
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private pendingRequests: Map<string, PendingRequest[]> = new Map();
  private responseCache: Map<string, CacheEntry> = new Map();
  private requestIdCounter = 0;

  constructor(config?: Partial<ApiClientConfig>) {
    const apiConfig = getApiConfig();
    this.config = {
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      maxRetries: apiConfig.retryAttempts,
      retryDelay: apiConfig.retryDelay,
      enableDeduplication: apiConfig.enableDeduplication,
      enableCaching: apiConfig.enableCaching,
      enableLogging: apiConfig.enableLogging,
      ...config
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      withCredentials: true, // Include cookies for refresh token
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': import.meta.env.VITE_VERSION || '1.0.0'
      }
    });

    this.setupInterceptors();
    this.setupCacheCleanup();
  }

  /**
   * Set access token for authenticated requests
   */
  public setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /**
   * Get current access token
   */
  public getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Clear cached responses
   */
  public clearCache(): void {
    this.responseCache.clear();
  }

  /**
   * Make authenticated GET request
   */
  public async get<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'GET', url, ...config });
  }

  /**
   * Make authenticated POST request
   */
  public async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'POST', url, data, ...config });
  }

  /**
   * Make authenticated PUT request
   */
  public async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PUT', url, data, ...config });
  }

  /**
   * Make authenticated DELETE request
   */
  public async delete<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'DELETE', url, ...config });
  }

  /**
   * Make authenticated PATCH request
   */
  public async patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PATCH', url, data, ...config });
  }

  /**
   * Upload file with progress tracking
   */
  public async upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
      ...config
    });
  }

  /**
   * Download file with progress tracking
   */
  public async download(
    url: string,
    filename: string,
    onProgress?: (progress: number) => void,
    config?: RequestConfig
  ): Promise<void> {
    const response = await this.request<Blob>({
      method: 'GET',
      url,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
      ...config
    });

    // Create download link
    const blob = new Blob([response.data as any]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Generate STS credentials for organization-specific resources
   */
  public async getStsCredentials(resourceType: string, duration?: number): Promise<any> {
    // Use data.briefcasebrain.com for auth endpoints
    try {
      const response = await fetch('https://data.briefcasebrain.com/api/v1/auth/sts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_type: resourceType,
          duration: duration || 3600
        })
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('STS credentials request failed');
      }
    } catch (error) {
      throw new Error(`STS credentials error: ${(error as Error).message}`);
    }
  }

  /**
   * Get current user information with organization context
   */
  public async getCurrentUser(): Promise<any> {
    // Use LakeFS user endpoint directly
    try {
      const response = await fetch('https://data.briefcasebrain.com/api/v1/user', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        return {
          success: true,
          data: {
            user: {
              id: userData.user?.id || 'unknown',
              email: userData.user?.email || 'user@briefcasebrain.com',
              firstName: userData.user?.display_name || 'User',
              lastName: '',
              organizationId: 'default'
            }
          }
        };
      } else {
        return { success: false, error: 'Failed to get user info' };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Make batch requests with controlled concurrency
   */
  public async batch<T = any>(
    requests: RequestConfig[],
    concurrency: number = 5
  ): Promise<Array<ApiResponse<T> | Error>> {
    const results: Array<ApiResponse<T> | Error> = [];
    const executing: Promise<any>[] = [];

    for (const request of requests) {
      const promise = this.request<T>(request)
        .then(result => {
          results.push(result);
          return result;
        })
        .catch(error => {
          results.push(error);
          return error;
        })
        .finally(() => {
          executing.splice(executing.indexOf(promise), 1);
        });

      executing.push(promise);

      if (executing.length >= concurrency) {
        await Promise.race(executing);
      }
    }

    await Promise.all(executing);
    return results;
  }

  // Private methods

  private async request<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    // Check cache first
    if (config.cacheKey && this.config.enableCaching) {
      const cached = this.getCachedResponse<T>(config.cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Handle request deduplication
    if (config.dedupeKey && this.config.enableDeduplication) {
      const existingRequests = this.pendingRequests.get(config.dedupeKey);
      if (existingRequests) {
        return new Promise<ApiResponse<T>>((resolve, reject) => {
          existingRequests.push({ resolve, reject });
        });
      } else {
        this.pendingRequests.set(config.dedupeKey, []);
      }
    }

    try {
      const response = await this.executeRequest<T>(config);

      // Cache successful responses
      if (config.cacheKey && this.config.enableCaching && response.success) {
        this.cacheResponse(config.cacheKey, response, config.cacheTTL || 300000); // 5 minutes default
      }

      // Resolve pending requests
      if (config.dedupeKey) {
        this.resolvePendingRequests(config.dedupeKey, response);
      }

      return response;

    } catch (error) {
      // Reject pending requests
      if (config.dedupeKey) {
        this.rejectPendingRequests(config.dedupeKey, error);
      }

      throw error;
    }
  }

  private async executeRequest<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    let lastError: Error;
    const retryCount = config.retryCount || 0;
    const maxRetries = this.config.maxRetries;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await this.client.request(config);
        return response.data;
      } catch (error) {
        lastError = error as Error;

        // Don't retry on certain errors
        if (this.shouldNotRetry(error as AxiosError) || attempt === maxRetries) {
          break;
        }

        // Wait before retry
        await this.delay(this.config.retryDelay * Math.pow(2, attempt));
      }
    }

    throw lastError!;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Generate request ID
        const requestId = `req_${Date.now()}_${++this.requestIdCounter}`;
        config.headers['X-Request-ID'] = requestId;

        // Add authentication header
        if (!config.skipAuth && this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }

        // Add security headers
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['X-Client-Type'] = 'web-dashboard';

        // Log request
        if (this.config.enableLogging) {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
            requestId,
            headers: this.sanitizeHeaders(config.headers),
            data: config.data
          });
        }

        return config;
      },
      (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response
        if (this.config.enableLogging) {
          console.log(`[API Response] ${response.status}`, {
            requestId: response.config.headers['X-Request-ID'],
            data: response.data
          });
        }

        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as RequestConfig;

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && !originalRequest.skipAuth && !originalRequest._retry) {
          originalRequest._retry = true;

          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAccessToken();
          }

          try {
            const refreshSuccess = await this.refreshPromise;
            this.refreshPromise = null;

            if (refreshSuccess) {
              // Retry original request with new token
              if (this.accessToken) {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
              }
              return this.client.request(originalRequest);
            } else {
              // Refresh failed, redirect to login
              this.handleAuthFailure();
            }
          } catch (refreshError) {
            this.refreshPromise = null;
            this.handleAuthFailure();
          }
        }

        // Log error
        if (this.config.enableLogging) {
          console.error('[API Response Error]', {
            requestId: originalRequest.headers?.['X-Request-ID'],
            status: error.response?.status,
            message: error.message,
            data: error.response?.data
          });
        }

        // Transform error
        const apiError = this.transformError(error);
        return Promise.reject(apiError);
      }
    );
  }

  private async refreshAccessToken(): Promise<boolean> {
    try {
      const response = await fetch('https://data.briefcasebrain.com/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.accessToken = data.data.access_token;
          // Clear cache when token is refreshed to prevent stale data
          this.clearCache();
          this.emit('auth:refresh_success', { timestamp: new Date() });
          return true;
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Emit refresh failure event for monitoring
      this.emit('auth:refresh_failed', error);
    }

    return false;
  }

  private handleAuthFailure(): void {
    this.accessToken = null;
    // Trigger app-wide logout
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  private transformError(error: AxiosError): ApiError {
    const response = error.response;

    if (response) {
      // Server responded with error status
      const data = response.data as any;
      return {
        code: data.error?.code || 'API_ERROR',
        message: data.error?.message || error.message,
        details: data.error?.details,
        request_id: response.headers['x-request-id']
      };
    } else if (error.request) {
      // Network error
      return {
        code: 'NETWORK_ERROR',
        message: 'Network connection failed. Please check your internet connection.',
        request_id: error.config?.headers?.['X-Request-ID']
      };
    } else {
      // Request setup error
      return {
        code: 'REQUEST_ERROR',
        message: error.message,
        request_id: error.config?.headers?.['X-Request-ID']
      };
    }
  }

  private shouldNotRetry(error: AxiosError): boolean {
    // Don't retry on client errors (4xx) except 401, 408, 429
    if (error.response) {
      const status = error.response.status;
      return status >= 400 && status < 500 && ![401, 408, 429].includes(status);
    }
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private getCachedResponse<T>(key: string): ApiResponse<T> | null {
    const cached = this.responseCache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.responseCache.delete(key);
    return null;
  }

  private cacheResponse<T>(key: string, response: ApiResponse<T>, ttl: number): void {
    this.responseCache.set(key, {
      data: response,
      timestamp: Date.now(),
      ttl
    });
  }

  private resolvePendingRequests<T>(key: string, response: ApiResponse<T>): void {
    const pending = this.pendingRequests.get(key);
    if (pending) {
      pending.forEach(({ resolve }) => resolve(response));
      this.pendingRequests.delete(key);
    }
  }

  private rejectPendingRequests(key: string, error: any): void {
    const pending = this.pendingRequests.get(key);
    if (pending) {
      pending.forEach(({ reject }) => reject(error));
      this.pendingRequests.delete(key);
    }
  }

  private sanitizeHeaders(headers: any): any {
    const sanitized = { ...headers };
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer ***';
    }
    return sanitized;
  }

  private setupCacheCleanup(): void {
    // Clean expired cache entries every 5 minutes
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.responseCache.entries()) {
        if (now - entry.timestamp >= entry.ttl) {
          this.responseCache.delete(key);
        }
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Emit events for monitoring and integration
   */
  private emit(event: string, data?: any): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(event, { detail: data }));
    }
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// React hook for API client
export const useApiClient = (): ApiClient => {
  return apiClient;
};

// Request configuration helpers
export const createCachedRequest = (url: string, cacheTTL = 300000): RequestConfig => ({
  url,
  cacheKey: url,
  cacheTTL
});

export const createDedupedRequest = (url: string, data?: any): RequestConfig => ({
  url,
  data,
  dedupeKey: `${url}:${JSON.stringify(data || {})}`
});

export default apiClient;